import React from "react";

import {
    FiCheckCircle,
    FiXCircle,
} from "react-icons/fi";

import "../DebitNotes.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const roundQuantity = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        1000
    ) / 1000;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatQuantity = (
    value
) => {
    const quantity =
        roundQuantity(
            value
        );

    return new Intl.NumberFormat(
        "en-IN",
        {
            maximumFractionDigits:
                3,
        }
    ).format(
        quantity
    );
};

const getItemName = (
    item
) =>
    item?.itemName ||
    item?.productName ||
    item?.name ||
    "Item";

const getHsnSac = (
    item
) =>
    item?.hsnSac ||
    item?.hsnCode ||
    item?.sacCode ||
    "";

const getRate = (
    item
) =>
    roundMoney(
        item?.rate ??
        item?.unitPrice ??
        item?.price ??
        0
    );

const getGrossAmount = (
    item
) =>
    roundMoney(
        item?.grossAmount ??
        0
    );

const getDiscountAmount = (
    item
) =>
    roundMoney(
        item?.discountAmount ??
        0
    );

const getTaxableAmount = (
    item
) =>
    roundMoney(
        item?.taxableAmount ??
        0
    );

const getTaxRate = (
    item
) =>
    roundMoney(
        item?.taxRate ??
        item?.gstRate ??
        0
    );

const getTaxAmount = (
    item
) =>
    roundMoney(
        item?.taxAmount ??
        0
    );

const getTotalAmount = (
    item
) =>
    roundMoney(
        item?.totalAmount ??
        item?.amount ??
        0
    );

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const DebitNoteItemsTable = ({
    items = [],
}) => {
    const records =
        Array.isArray(
            items
        )
            ? items
            : [];

    if (
        records.length ===
        0
    ) {
        return (
            <div className="debit-note-empty">
                <p>
                    No returned items found.
                </p>
            </div>
        );
    }

    return (
        <div className="debit-note-items-table-wrapper">
            <table className="debit-note-items-table">
                <thead>
                    <tr>
                        <th>
                            #
                        </th>

                        <th>
                            Item
                        </th>

                        <th className="text-right">
                            Purchased Qty
                        </th>

                        <th className="text-right">
                            Already Returned
                        </th>

                        <th className="text-right">
                            Return Qty
                        </th>

                        <th className="text-right">
                            Rate
                        </th>

                        <th className="text-right">
                            Gross
                        </th>

                        <th className="text-right">
                            Discount
                        </th>

                        <th className="text-right">
                            Taxable
                        </th>

                        <th className="text-right">
                            Tax
                        </th>

                        <th>
                            Stock
                        </th>

                        <th className="text-right">
                            Amount
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {records.map(
                        (
                            item,
                            index
                        ) => {
                            const reduceStock =
                                item?.reduceStock !==
                                false;

                            const stockReduced =
                                item?.stockReduced ===
                                true;

                            return (
                                <tr
                                    key={
                                        item?._id ||
                                        item?.purchaseBillItemId ||
                                        index
                                    }
                                >
                                    <td>
                                        {index + 1}
                                    </td>

                                    <td>
                                        <div className="create-debit-note-item">
                                            <strong>
                                                {getItemName(
                                                    item
                                                )}
                                            </strong>

                                            {getHsnSac(
                                                item
                                            ) && (
                                                    <span>
                                                        HSN/SAC:{" "}
                                                        {getHsnSac(
                                                            item
                                                        )}
                                                    </span>
                                                )}

                                            {item?.description && (
                                                <span>
                                                    {
                                                        item.description
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="text-right">
                                        {formatQuantity(
                                            item
                                                ?.originalQuantity
                                        )}
                                    </td>

                                    <td className="text-right">
                                        {formatQuantity(
                                            item
                                                ?.alreadyReturnedQuantity
                                        )}
                                    </td>

                                    <td className="text-right">
                                        <strong>
                                            {formatQuantity(
                                                item
                                                    ?.returnQuantity
                                            )}
                                        </strong>
                                    </td>

                                    <td className="text-right">
                                        {formatCurrency(
                                            getRate(
                                                item
                                            )
                                        )}
                                    </td>

                                    <td className="text-right">
                                        {formatCurrency(
                                            getGrossAmount(
                                                item
                                            )
                                        )}
                                    </td>

                                    <td className="text-right">
                                        {formatCurrency(
                                            getDiscountAmount(
                                                item
                                            )
                                        )}
                                    </td>

                                    <td className="text-right">
                                        {formatCurrency(
                                            getTaxableAmount(
                                                item
                                            )
                                        )}
                                    </td>

                                    <td className="text-right">
                                        <div>
                                            {formatCurrency(
                                                getTaxAmount(
                                                    item
                                                )
                                            )}

                                            {getTaxRate(
                                                item
                                            ) >
                                                0 && (
                                                    <small
                                                        style={{
                                                            display:
                                                                "block",

                                                            marginTop:
                                                                "2px",

                                                            color:
                                                                "#94a3b8",

                                                            fontSize:
                                                                "8px",
                                                        }}
                                                    >
                                                        {getTaxRate(
                                                            item
                                                        )}
                                                        %
                                                    </small>
                                                )}
                                        </div>
                                    </td>

                                    <td>
                                        {!reduceStock ? (
                                            <span
                                                className="debit-note-status debit-note-status-draft"
                                            >
                                                <FiXCircle />

                                                Not Reduced
                                            </span>
                                        ) : stockReduced ? (
                                            <span
                                                className="debit-note-status debit-note-status-confirmed"
                                            >
                                                <FiCheckCircle />

                                                Reduced
                                            </span>
                                        ) : (
                                            <span
                                                className="debit-note-status debit-note-status-draft"
                                            >
                                                Pending
                                            </span>
                                        )}
                                    </td>

                                    <td className="text-right">
                                        <strong
                                            style={{
                                                color:
                                                    "#1d4ed8",
                                            }}
                                        >
                                            {formatCurrency(
                                                getTotalAmount(
                                                    item
                                                )
                                            )}
                                        </strong>
                                    </td>
                                </tr>
                            );
                        }
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default DebitNoteItemsTable;